import React, { useState, useEffect, useRef, Suspense } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSave, faTrash } from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import { useNotification } from "@cakrawalajakarta/shared-components";
import {
  getFirestore,
  getDoc,
  doc,
  setDoc,
  getDocs,
  collection,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { motion } from "framer-motion";

// Layout utility
const Section = ({ title, children, className = "" }) => (
  <section
    className={`bg-white border border-yellow-400 rounded-xl p-4 mb-4 ${className}`}
  >
    {title && (
      <h2 className="text-lg font-bold text-yellow-600 mb-4 font-oxanium">
        {title}
      </h2>
    )}
    {children}
  </section>
);

// Loading skeleton
const LoadingSkeleton = () => (
  <div className="space-y-8">
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <div className="h-8 w-1/4 bg-slate-200 rounded animate-pulse mb-4"></div>
      <div className="space-y-3">
        <div className="h-4 w-3/4 bg-slate-200 rounded animate-pulse"></div>
        <div className="h-4 w-1/2 bg-slate-200 rounded animate-pulse"></div>
      </div>
    </div>
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <div className="h-8 w-1/3 bg-slate-200 rounded animate-pulse mb-4"></div>
      <div className="space-y-3">
        <div className="h-4 w-2/3 bg-slate-200 rounded animate-pulse"></div>
        <div className="h-4 w-3/4 bg-slate-200 rounded animate-pulse"></div>
      </div>
    </div>
  </div>
);

export const Admintiming = () => {
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const db = getFirestore();

  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventStatus, setEventStatus] = useState("ongoing");
  const [rounds, setRounds] = useState([]);
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [eventSaved, setEventSaved] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [activeEventId, setActiveEventId] = useState("");
  const [doubleInputInterval, setDoubleInputInterval] = useState("");
  const isSaving = useRef(false);

  // Load events from Firestore
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setIsLoading(true);
        // Fetch events from timingEvents (only verticalchallenge)
        const eventDocRef = doc(db, "timingEvents", "verticalchallenge");
        const eventDoc = await getDoc(eventDocRef);
        const eventsData = [];
        if (eventDoc.exists()) {
          eventsData.push({
            eventId: eventDoc.id,
            ...eventDoc.data(),
          });
        }
        setEvents(eventsData);

        // Load activeEventId from localStorage
        const activeId = localStorage.getItem("activeEventId") || "";
        setActiveEventId(activeId);

        // If there is an activeEventId, select that event
        if (activeId) {
          setSelectedEventId(activeId);
        }

        if (eventsData.length === 0) {
          setMessage(
            "No events found in the database. Please create a new event."
          );
        }
      } catch (err) {
        console.error("Error loading events:", err);
        let errorMessage = "Failed to load events.";
        if (err.code === "permission-denied") {
          errorMessage =
            "Access denied: Insufficient permissions to access data.";
        }
        showNotification("error", errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, [db, showNotification]);

  // Load event data when selectedEventId changes
  useEffect(() => {
    if (!selectedEventId || isSaving.current) return;

    const event = events.find((e) => e.eventId === selectedEventId);
    if (event) {
      setEventName(event.eventName || "");
      setEventDate(event.date || "");
      setEventStatus(event.status || "ongoing");
      setDoubleInputInterval(event.doubleInputInterval ?? "");
      setRounds(
        event.rounds
          ? event.rounds.map((round) => ({
              id: round.id,
              name: round.name,
              startTime: round.startTime
                ? moment(round.startTime).format("HH:mm")
                : "",
              cot: round.cot ? moment(round.cot).format("HH:mm") : "",
            }))
          : []
      );
      setEventSaved(true);
      setMessage(`Event "${event.eventName}" loaded for editing.`);
      console.log("Event loaded:", event.eventId);
    } else if (!selectedEventId) {
      setEventName("");
      setEventDate("");
      setEventStatus("ongoing");
      setDoubleInputInterval("");
      setRounds([]);
      setEventSaved(false);
      setMessage("Please fill in details to create a new event.");
    }
  }, [selectedEventId, events]);

  const handleAddRound = () => {
    const roundCount = rounds.length + 1;
    setRounds([
      ...rounds,
      {
        id: `round${roundCount}`,
        name: `Round ${roundCount}`,
        startTime: "",
        cot: "",
      },
    ]);
  };

  const handleRemoveRound = (roundIndex) => {
    const newRounds = rounds.filter((_, index) => index !== roundIndex);
    setRounds(newRounds);
  };

  const handleSetActiveEvent = async () => {
    if (!selectedEventId) {
      setMessage("Please select an event to set as active.");
      return;
    }

    try {
      setIsLoading(true);
      localStorage.setItem("activeEventId", selectedEventId); // Save to localStorage
      setActiveEventId(selectedEventId);
      const event = events.find((e) => e.eventId === selectedEventId);
      setMessage(
        `Event "${event?.eventName || selectedEventId}" set as active event.`
      );
      console.log("Active event set:", selectedEventId);
    } catch (error) {
      setMessage(`Error setting active event: ${error.message}`);
      console.error("Error setting active event:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivateEvent = async () => {
    try {
      setIsLoading(true);
      localStorage.removeItem("activeEventId"); // Remove from localStorage
      setActiveEventId("");
      setMessage("Active event deactivated.");
      console.log("Active event deactivated");
    } catch (error) {
      setMessage(`Error deactivating event: ${error.message}`);
      console.error("Error deactivating event:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = (newStatus) => {
    setEventStatus(newStatus);
    setMessage(`Event status changed to "${newStatus.toUpperCase()}".`);
    console.log("Event status changed to:", newStatus);
  };

  const handleSaveEvent = async () => {
    if (!eventName || !eventDate) {
      setMessage("Event name and date are required.");
      console.log("Validation failed: Event name or date missing");
      return;
    }
    if (
      rounds.some(
        (round) =>
          !round.id ||
          !round.name ||
          !round.startTime ||
          !round.cot ||
          !moment(round.startTime, "HH:mm", true).isValid() ||
          !moment(round.cot, "HH:mm", true).isValid() ||
          moment(`${eventDate}T${round.cot}:00+07:00`).isSameOrBefore(
            moment(`${eventDate}T${round.startTime}:00+07:00`)
          )
      )
    ) {
      setMessage(
        "All rounds must have an ID, name, valid start time, and valid cut-off time (cut-off must be after start time)."
      );
      console.log("Validation failed: Incomplete or invalid round data");
      return;
    }

    if (doubleInputInterval === "" || Number(doubleInputInterval) < 0) {
      setMessage(
        "Double input prevention interval must be a non-negative number."
      );
      console.log("Validation failed: Invalid doubleInputInterval");
      return;
    }

    isSaving.current = true;
    setIsLoading(true);
    try {
      const eventId = "verticalchallenge";
      const eventDocRef = doc(db, "timingEvents", eventId);

      // Fetch existing document to retain createdAt
      let createdAt = serverTimestamp();
      if (eventSaved) {
        const existingDoc = await getDoc(eventDocRef);
        if (existingDoc.exists()) {
          createdAt = existingDoc.data().createdAt || serverTimestamp();
        }
      }

      const eventData = {
        eventId,
        eventName,
        date: eventDate,
        status: eventStatus,
        doubleInputInterval: Number(doubleInputInterval),
        rounds: rounds.map((round) => ({
          id: round.id,
          name: round.name,
          startTime: moment(
            `${eventDate}T${round.startTime}:00+07:00`,
            "YYYY-MM-DDTHH:mm:ssZ"
          ).toISOString(),
          cot: moment(
            `${eventDate}T${round.cot}:00+07:00`,
            "YYYY-MM-DDTHH:mm:ssZ"
          ).toISOString(),
        })),
        createdAt,
        updatedAt: serverTimestamp(),
      };

      // Send to backend
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/timing/save-event`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(eventData),
        }
      );

      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(
          responseData.message || "Failed to save event to backend."
        );
      }

      // Save to Firestore as backup
      await setDoc(eventDocRef, eventData, { merge: true });
      setMessage(
        `Event "${eventName}" ${
          eventSaved ? "updated" : "created"
        } successfully!`
      );
      setEventSaved(true);
      console.log("Event saved to Firestore:", eventId);

      // Update event list
      const eventDoc = await getDoc(eventDocRef);
      const eventsData = [];
      if (eventDoc.exists()) {
        eventsData.push({
          eventId: eventDoc.id,
          ...eventDoc.data(),
        });
      }
      setEvents(eventsData);
      setSelectedEventId(eventId);

      if (file) {
        try {
          const reader = new FileReader();
          reader.onload = async (event) => {
            const text = event.target.result;
            const rows = text.split("\n").slice(1);
            const participants = rows
              .map((row) => {
                const [bib, name, gender] = row
                  .split(",")
                  .map((item) => item.trim());
                if (!bib || !name || !gender) {
                  return null;
                }
                return {
                  bib,
                  name,
                  gender: gender.toLowerCase(),
                  eventId,
                };
              })
              .filter((p) => p !== null);

            if (participants.length === 0) {
              setMessage(
                "No valid participants found in CSV. Please check CSV format."
              );
              isSaving.current = false;
              setIsLoading(false);
              return;
            }

            const payload = { participants };

            const uploadResponse = await fetch(
              `${
                import.meta.env.VITE_API_BASE_URL
              }/api/timing/upload-participants`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              }
            );

            const uploadData = await uploadResponse.json();
            if (uploadResponse.ok) {
              setMessage(
                `Event "${eventName}" ${
                  eventSaved ? "updated" : "created"
                } and participants uploaded successfully!`
              );
              setFile(null);
            } else {
              setMessage(
                `Event saved, but error uploading participants: ${
                  uploadData.message || "Failed to upload participants."
                } (Status: ${uploadResponse.status})`
              );
            }
            isSaving.current = false;
            setIsLoading(false);
          };
          reader.onerror = () => {
            setMessage("Error reading CSV file.");
            isSaving.current = false;
            setIsLoading(false);
          };
          reader.readAsText(file);
        } catch (error) {
          setMessage(
            `Event saved, but error uploading participants: ${error.message}`
          );
          isSaving.current = false;
          setIsLoading(false);
        }
      } else {
        isSaving.current = false;
        setIsLoading(false);
      }
    } catch (error) {
      setMessage(`Error saving event: ${error.message}`);
      setEventSaved(false);
      isSaving.current = false;
      setIsLoading(false);
      console.error("Error saving event:", error);
    }
  };

  return (
    <Suspense fallback={<LoadingSkeleton />}>
      <motion.div
        className="min-h-screen bg-yellow-50 flex flex-col items-center py-20 px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="w-full max-w-7xl">
          <motion.h1
            className="text-3xl md:text-4xl font-extrabold text-yellow-600 tracking-tight font-oxanium mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Event Configuration
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="mb-4">
              <label className="block text-sm font-semibold text-yellow-600 mb-1">
                Create or Edit Event
              </label>
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 relative">
                  <select
                    value={selectedEventId}
                    onChange={(e) => setSelectedEventId(e.target.value)}
                    className="w-full p-2 pr-8 border border-yellow-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-yellow-100 appearance-none"
                    style={{ backgroundPosition: "right 1rem center" }}
                    disabled={isLoading}
                  >
                    <option value="">Create New Event</option>
                    {events.map((event) => (
                      <option key={event.eventId} value={event.eventId}>
                        {event.eventName} (
                        {new Date(event.date).toLocaleDateString("en-US")})
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-yellow-600">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </div>
                <div className="flex gap-4 md:justify-end">
                  <button
                    onClick={handleSetActiveEvent}
                    className="px-4 py-2 bg-yellow-400 text-white rounded-xl hover:bg-yellow-600 transition-colors disabled:bg-yellow-400"
                    disabled={!selectedEventId || isLoading}
                  >
                    Set as Active
                  </button>
                  <button
                    onClick={handleDeactivateEvent}
                    className="px-4 py-2 bg-rose-400 text-white rounded-xl hover:bg-rose-600 transition-colors disabled:bg-yellow-400"
                    disabled={!activeEventId || isLoading}
                  >
                    Deactivate Event
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4 text-yellow-600">
              <Section title="Event Details">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-yellow-600 mb-1">
                      Event Name
                    </label>
                    <input
                      type="text"
                      value={eventName}
                      onChange={(e) => setEventName(e.target.value)}
                      className="w-full p-3 border border-yellow-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-yellow-50"
                      placeholder="Enter event name"
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-yellow-600 mb-1">
                      Upload Participants (CSV)
                    </label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setFile(e.target.files[0])}
                      className="w-full p-3 border border-yellow-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-yellow-50"
                      disabled={isLoading}
                    />
                    {file && (
                      <div className="mt-2 text-xs text-yellow-600 truncate">
                        Selected: {file.name}
                      </div>
                    )}
                  </div>
                </div>
              </Section>
              <Section title="Event Settings">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-yellow-600 mb-1">
                      Event Date
                    </label>
                    <input
                      type="date"
                      value={eventDate}
                      onChange={(e) => setEventDate(e.target.value)}
                      className="w-full p-3 border border-yellow-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-yellow-50"
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-yellow-600 mb-1">
                      Event Status
                    </label>
                    <div className="flex gap-4 mt-2">
                      <button
                        type="button"
                        onClick={() => handleStatusChange("ongoing")}
                        className={`flex-1 px-4 py-2 rounded-xl font-normal border transition-colors
                          ${
                            eventStatus === "ongoing"
                              ? "bg-yellow-400 text-white border-yellow-400"
                              : "bg-yellow-50 text-yellow-600 border-yellow-400 hover:bg-yellow-100"
                          }
                        `}
                        disabled={isLoading}
                      >
                        Ongoing
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusChange("completed")}
                        className={`flex-1 px-4 py-2 rounded-xl font-normal border transition-colors
                          ${
                            eventStatus === "completed"
                              ? "bg-yellow-400 text-white border-yellow-400"
                              : "bg-yellow-50 text-yellow-600 border-yellow-400 hover:bg-yellow-100"
                          }
                        `}
                        disabled={isLoading}
                      >
                        Completed
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-yellow-600 mb-1">
                      Double Input Prevention Interval (minutes)
                    </label>
                    <input
                      type="number"
                      value={doubleInputInterval}
                      onChange={(e) => setDoubleInputInterval(e.target.value)}
                      className="w-full p-3 border border-yellow-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-yellow-50"
                      placeholder="Enter interval (minutes)"
                      min={0}
                      disabled={isLoading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Set to 0 to disable double input prevention.
                    </p>
                  </div>
                </div>
              </Section>
            </div>

            <Section title="Rounds">
              {message && (
                <div
                  className={`mb-4 text-center text-base font-medium ${
                    message.includes("Error") || message.includes("Failed")
                      ? "text-red-400"
                      : "text-lime-400"
                  }`}
                >
                  {message}
                </div>
              )}
              {isLoading && (
                <div className="mb-4 text-center text-base font-medium text-yellow-600">
                  Processing...
                </div>
              )}
              <div className="space-y-4">
                {rounds.length > 0 && (
                  <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-yellow-600">
                        Round ID
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-yellow-600">
                        Round Name
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-yellow-600">
                        Start Time (HH:mm)
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-yellow-600">
                        Cut-Off Time (HH:mm)
                      </label>
                    </div>
                  </div>
                )}
                {rounds.map((round, roundIndex) => (
                  <div
                    key={round.id}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
                  >
                    <div className="space-y-1">
                      <label className="block text-sm font-semibold text-yellow-600 sm:hidden">
                        Round ID
                      </label>
                      <input
                        type="text"
                        value={round.id}
                        readOnly
                        className="w-full p-3 border border-yellow-400 rounded-xl bg-yellow-100 focus:outline-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-semibold text-yellow-600 sm:hidden">
                        Round Name
                      </label>
                      <input
                        type="text"
                        value={round.name}
                        onChange={(e) => {
                          const newRounds = [...rounds];
                          newRounds[roundIndex].name = e.target.value;
                          setRounds(newRounds);
                        }}
                        className="w-full p-3 border border-yellow-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-yellow-50"
                        disabled={isLoading}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-semibold text-yellow-600 sm:hidden">
                        Start Time (HH:mm)
                      </label>
                      <input
                        type="time"
                        value={round.startTime}
                        onChange={(e) => {
                          const newRounds = [...rounds];
                          newRounds[roundIndex].startTime = e.target.value;
                          setRounds(newRounds);
                        }}
                        className="w-full p-3 border border-yellow-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-yellow-50"
                        disabled={isLoading}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-semibold text-yellow-600 sm:hidden">
                        Cut-Off Time (HH:mm)
                      </label>
                      <div className="flex items-center">
                        <div className="flex-1">
                          <input
                            type="time"
                            value={round.cot}
                            onChange={(e) => {
                              const newRounds = [...rounds];
                              newRounds[roundIndex].cot = e.target.value;
                              setRounds(newRounds);
                            }}
                            className="w-full p-3 border border-yellow-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-yellow-50"
                            disabled={isLoading}
                          />
                        </div>
                        <button
                          onClick={() => handleRemoveRound(roundIndex)}
                          className="ml-2 p-3 text-red-500 hover:text-red-700"
                          disabled={isLoading}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {rounds.length === 0 && (
                  <div className="text-center text-sm text-gray-500">
                    No rounds added yet. Click "Add Round" to start.
                  </div>
                )}
              </div>
              <div className="flex gap-4 mt-4">
                <button
                  onClick={handleAddRound}
                  className="px-4 py-2 bg-lime-400 text-white rounded-xl hover:bg-lime-600 transition-colors flex items-center justify-center gap-2 text-base font-normal disabled:bg-yellow-400"
                  type="button"
                  disabled={isLoading}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Round
                </button>
                <button
                  onClick={handleSaveEvent}
                  className="px-4 py-2 bg-yellow-400 text-white rounded-xl hover:bg-yellow-600 transition-colors flex items-center justify-center gap-2 text-base font-normal disabled:bg-yellow-400"
                  type="button"
                  disabled={isLoading}
                >
                  <FontAwesomeIcon icon={faSave} />{" "}
                  {selectedEventId ? "Update Event" : "Save Event"}
                </button>
              </div>
            </Section>
          </motion.div>
        </div>
      </motion.div>
    </Suspense>
  );
};

export default Admintiming;
